import React from 'react';
import clsx from 'clsx';
import { SUPPORTED_LOCALES, getLocaleCatalogEntry } from '@/i18n/localeCatalog';

function LocaleFlagGraphic({ flagSrc, title }) {
  return (
    <img
      src={flagSrc}
      alt=""
      width={22}
      height={15}
      decoding="async"
      title={title}
      className="shrink-0 rounded-[2px] border border-slate-300/90 object-cover shadow-sm dark:border-slate-500/80"
    />
  );
}

/**
 * Chọn locale: cờ dùng SVG trong `public/flags/` (ổn định trên mọi OS); emoji cờ trong nhiều môi trường chỉ hiện chữ VN/US.
 *
 * @param {{ value: string, onChange: (id: string) => void, ariaLabel: string, className?: string }} props
 */
export function LocaleSelect({ value, onChange, ariaLabel, className }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const listId = React.useId();
  const current = getLocaleCatalogEntry(value);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={clsx('relative shrink-0', className)}>
      <button
        type="button"
        id={`${listId}-trigger`}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'flex h-9 min-w-[10.5rem] items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-left text-xs font-bold text-slate-800 shadow-sm outline-none transition',
          'hover:border-cyan-300 focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-1 focus-visible:ring-offset-slate-50',
          'dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-cyan-600 dark:focus-visible:ring-offset-slate-900',
          open && 'border-cyan-400 ring-2 ring-cyan-400/40 dark:border-cyan-600',
        )}
      >
        <LocaleFlagGraphic flagSrc={current.flagSrc} title={current.nativeName} />
        <span className="min-w-0 flex-1 truncate">
          <span className="tabular-nums">{current.id.toUpperCase()}</span>
          <span className="ml-1 font-semibold text-slate-500 dark:text-slate-400">·</span>
          <span className="ml-1 font-semibold text-slate-600 dark:text-slate-300">{current.nativeName}</span>
        </span>
        <span className="material-symbols-outlined shrink-0 text-lg text-slate-500 dark:text-slate-400" aria-hidden>
          {open ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {open ? (
        <div
          id={listId}
          role="listbox"
          aria-labelledby={`${listId}-trigger`}
          className="absolute right-0 top-[calc(100%+0.25rem)] z-[80] min-w-[12.5rem] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-600 dark:bg-slate-800"
        >
          {SUPPORTED_LOCALES.map((loc) => {
            const selected = loc.id === value;
            return (
              <button
                key={loc.id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(loc.id);
                  setOpen(false);
                }}
                className={clsx(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-bold transition',
                  selected
                    ? 'bg-cyan-50 text-cyan-900 dark:bg-cyan-950/50 dark:text-cyan-50'
                    : 'text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-700/80',
                )}
              >
                <LocaleFlagGraphic flagSrc={loc.flagSrc} title={loc.nativeName} />
                <span className="tabular-nums">{loc.id.toUpperCase()}</span>
                <span className="font-semibold text-slate-500 dark:text-slate-400">—</span>
                <span className="min-w-0 flex-1 truncate font-semibold">{loc.nativeName}</span>
                {selected ? (
                  <span className="material-symbols-outlined shrink-0 text-base text-cyan-600 dark:text-cyan-400" aria-hidden>
                    check
                  </span>
                ) : (
                  <span className="w-5 shrink-0" aria-hidden />
                )}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
